import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsFeeStructure = ({ onNavigate, userInfo = {} }) => {
    // Accounts User Data State
    const [accountsData, setAccountsData] = useState({
        name: userInfo?.name || "Accounts User",
        department: "Accounts Department",
        designation: "Accounts Officer"
    });

    // Fee Structure Data
    const [feeStructures, setFeeStructures] = useState([]);
    const [loading, setLoading] = useState(false);

    // Form State for Add/Edit
    const [showAddModal, setShowAddModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentStructure, setCurrentStructure] = useState({
        id: null,
        academic_year: new Date().getFullYear(),
        department: '',
        semester: '',
        batch: '',
        year: '',
        // One-time fees (at admission)
        admission_fee: 0,
        uniform_fee: 0,
        skill_fee: 0,
        registration_fee: 0,
        // Semester-wise fees - stored per semester
        semester_fees: {
            '1': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
            '2': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
            '3': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
            '4': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
            '5': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
            '6': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
            '7': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
            '8': { tuition_fee: 0, exam_fee: 0, other_fees: 0 }
        }
    });

    // Active semester tab state
    const [activeSemester, setActiveSemester] = useState('1');

    // Filter State
    const [filters, setFilters] = useState({
        department: '',
        year: ''
    });

    const [batches, setBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);

    // Available options for dropdowns - FETCHED FROM DB
    const [departments, setDepartments] = useState([]);
    const [years, setYears] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [yearsLoading, setYearsLoading] = useState(false);

    // API HELPER FUNCTION
    const apiCall = async (endpoint, options = {}) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        });

        if (!response.ok) {
            if (response.status === 401) {
                localStorage.removeItem('token');
                sessionStorage.removeItem('token');
                throw new Error('Session expired');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    };

    // FETCH DEPARTMENTS
    const fetchDepartments = async () => {
        try {
            setDepartmentsLoading(true);
            const result = await apiCall('/departments');

            if (result.success && result.data) {
                setDepartments(result.data);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        } finally {
            setDepartmentsLoading(false);
        }
    };

    // FETCH YEARS FROM DATABASE (with fallback)
    const fetchYears = async () => {
        try {
            setYearsLoading(true);
            // Try to fetch from API first
            try {
                const result = await apiCall('/years');
                if (result.success && result.data) {
                    setYears(result.data);
                    return;
                }
            } catch (apiError) {
                console.log('Years API endpoint not available, using generated years');
            }

            // Fallback: Generate years dynamically
            const currentYear = new Date().getFullYear();
            const generatedYears = [];
            for (let i = 0; i <= 3; i++) {
                const year = currentYear + i;
                generatedYears.push({
                    value: `${year}`,
                    label: `${year}`
                });
            }
            setYears(generatedYears);
        } catch (error) {
            console.error('Error fetching years:', error);
            // Even if error, set default years
            const currentYear = new Date().getFullYear();
            setYears([
                { value: `${currentYear}`, label: `${currentYear}` },
                { value: `${currentYear + 1}`, label: `${currentYear + 1}` },
                { value: `${currentYear + 2}`, label: `${currentYear + 2}` },
                { value: `${currentYear + 3}`, label: `${currentYear + 3}` }
            ]);
        } finally {
            setYearsLoading(false);
        }
    };

    // Semester options (Static - matches Accountsfeemanage.js)
    const semesters = [
        { value: '1st Sem', label: '1st Semester' },
        { value: '2nd Sem', label: '2nd Semester' },
        { value: '3rd Sem', label: '3rd Semester' },
        { value: '4th Sem', label: '4th Semester' },
        { value: '5th Sem', label: '5th Semester' },
        { value: '6th Sem', label: '6th Semester' },
        { value: '7th Sem', label: '7th Semester' },
        { value: '8th Sem', label: '8th Semester' }
    ];

    // Fetch Batches
    const fetchBatches = async (department, year) => {
        if (!department) {
            setBatches([]);
            return;
        }
        try {
            setBatchesLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            // Assuming this endpoint exists and works
            let apiUrl = `${API_LINK}/api/admin/batches?department=${encodeURIComponent(department)}`;
            const response = await fetch(apiUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success && result.data) {
                setBatches(result.data);
            } else {
                setBatches([]);
            }
        } catch (error) {
            console.error('Error fetching batches:', error);
            setBatches([]);
        } finally {
            setBatchesLoading(false);
        }
    };

    // Initial Data Fetch
    useEffect(() => {
        fetchDepartments();
        fetchYears();

        // Simulate fetching existing structures (Replace with real API later)
        setFeeStructures([
            {
                id: 1,
                academic_year: 2024,
                department: 'CSE',
                batch: 'B.TECH CSE (AI-ML) Section II',
                year: '1st year',
                semester: '1st Sem',
                admission_fee: 5000,
                uniform_fee: 3000,
                skill_fee: 2000,
                registration_fee: 1000,
                semester_fees: {
                    '1': { tuition_fee: 35000, exam_fee: 2500, other_fees: 0 },
                    '2': { tuition_fee: 35000, exam_fee: 2500, other_fees: 0 },
                    '3': { tuition_fee: 35000, exam_fee: 2500, other_fees: 0 },
                    '4': { tuition_fee: 35000, exam_fee: 2500, other_fees: 0 },
                    '5': { tuition_fee: 35000, exam_fee: 2500, other_fees: 0 },
                    '6': { tuition_fee: 35000, exam_fee: 2500, other_fees: 0 },
                    '7': { tuition_fee: 35000, exam_fee: 2500, other_fees: 0 },
                    '8': { tuition_fee: 35000, exam_fee: 2500, other_fees: 0 }
                }
            },
            {
                id: 2,
                academic_year: 2024,
                department: 'ECE',
                batch: 'B.Tech-2023-2027(ECE-I)',
                year: '2nd year',
                semester: '3rd Sem',
                admission_fee: 5000,
                uniform_fee: 3000,
                skill_fee: 2000,
                registration_fee: 1000,
                semester_fees: {
                    '1': { tuition_fee: 32000, exam_fee: 2500, other_fees: 0 },
                    '2': { tuition_fee: 32000, exam_fee: 2500, other_fees: 0 },
                    '3': { tuition_fee: 32000, exam_fee: 2500, other_fees: 0 },
                    '4': { tuition_fee: 32000, exam_fee: 2500, other_fees: 0 },
                    '5': { tuition_fee: 32000, exam_fee: 2500, other_fees: 0 },
                    '6': { tuition_fee: 32000, exam_fee: 2500, other_fees: 0 },
                    '7': { tuition_fee: 32000, exam_fee: 2500, other_fees: 0 },
                    '8': { tuition_fee: 32000, exam_fee: 2500, other_fees: 0 }
                }
            }
        ]);
    }, []);

    // Handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentStructure(prev => {
            const updated = { ...prev, [name]: value };

            if (name === 'department') {
                fetchBatches(value, prev.year);
                updated.batch = ''; // Reset batch on dept change
            } else if (name === 'year') {
                if (updated.department) {
                    fetchBatches(updated.department, value);
                }
            }
            return updated;
        });
    };

    // Handle semester-specific fee changes
    const handleSemesterFeeChange = (semesterNum, fieldName, value) => {
        setCurrentStructure(prev => ({
            ...prev,
            semester_fees: {
                ...prev.semester_fees,
                [semesterNum]: {
                    ...prev.semester_fees[semesterNum],
                    [fieldName]: value
                }
            }
        }));
    };


    const handleSaveStructure = (e) => {
        e.preventDefault();
        // Logic to save/update
        const newEntry = { ...currentStructure, id: isEditing ? currentStructure.id : Date.now() };

        if (isEditing) {
            setFeeStructures(prev => prev.map(item => item.id === newEntry.id ? newEntry : item));
        } else {
            setFeeStructures(prev => [...prev, newEntry]);
        }
        setShowAddModal(false);
        resetForm();
    };

    const handleEditClick = (structure) => {
        // Ensure semester_fees exists for backward compatibility with old data
        const updatedStructure = {
            ...structure,
            semester_fees: structure.semester_fees || {
                '1': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '2': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '3': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '4': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '5': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '6': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '7': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '8': { tuition_fee: 0, exam_fee: 0, other_fees: 0 }
            }
        };
        setCurrentStructure(updatedStructure);
        setIsEditing(true);
        setShowAddModal(true);
    };

    const handleDeleteClick = (id) => {
        if (window.confirm("Are you sure you want to delete this fee structure?")) {
            setFeeStructures(prev => prev.filter(item => item.id !== id));
        }
    };

    const resetForm = () => {
        setCurrentStructure({
            id: null,
            academic_year: new Date().getFullYear(),
            department: '',
            semester: '',
            batch: '',
            year: '',
            // One-time fees (at admission)
            admission_fee: 0,
            uniform_fee: 0,
            skill_fee: 0,
            registration_fee: 0,
            // Semester-wise fees
            semester_fees: {
                '1': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '2': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '3': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '4': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '5': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '6': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '7': { tuition_fee: 0, exam_fee: 0, other_fees: 0 },
                '8': { tuition_fee: 0, exam_fee: 0, other_fees: 0 }
            }
        });
        setIsEditing(false);
        setActiveSemester('1'); // Reset to 1st semester
    };

    const totalFee = (structure) => {
        // Calculate one-time fees
        const oneTimeFees = parseInt(structure.admission_fee || 0) +
            parseInt(structure.uniform_fee || 0) +
            parseInt(structure.skill_fee || 0) +
            parseInt(structure.registration_fee || 0);

        // Calculate total semester fees across all semesters
        let semesterFeesTotal = 0;
        if (structure.semester_fees) {
            Object.values(structure.semester_fees).forEach(semFees => {
                semesterFeesTotal += parseInt(semFees.tuition_fee || 0) +
                    parseInt(semFees.exam_fee || 0) +
                    parseInt(semFees.other_fees || 0);
            });
        }

        return oneTimeFees + semesterFeesTotal;
    };

    return (
        <Layout role="accounts"
            onNavigate={onNavigate}
            activePage="accounts-fee-structures"
            pageTitle="Fee Structures"
            accountsData={accountsData}
        >
            {/* Header Card */}\n            <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
            }}>
                <div>
                    <h1 style={{
                        margin: '0 0 0.5rem 0',
                        color: '#333',
                        fontSize: '2rem',
                        fontWeight: 'bold'
                    }}>
                        Fee Structure Definitions
                    </h1>
                    <p style={{
                        margin: 0,
                        color: '#666',
                        fontSize: '1.1rem',
                        lineHeight: 1.6
                    }}>
                        Define standard fees for departments and semesters.
                    </p>
                </div>
                <button
                    onClick={() => { resetForm(); setShowAddModal(true); }}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#238548',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        boxShadow: '0 4px 6px rgba(35, 133, 72, 0.2)',
                        transition: 'all 0.2s ease'
                    }}
                >
                    + Add New Structure
                </button>
            </div>

            {/* Table */}
            <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{
                        background: 'linear-gradient(135deg, #238548 0%, #2e7d32 100%)',
                        color: '#ffffff'
                    }}>
                        <tr>
                            <th style={{ padding: '1rem', fontWeight: 600 }}>Department</th>
                            <th style={{ padding: '1rem', fontWeight: 600 }}>Batch</th>
                            <th style={{ padding: '1rem', fontWeight: 600 }}>Year</th>
                            <th style={{ padding: '1rem', fontWeight: 600, textAlign: 'right' }}>Total Fee</th>
                            <th style={{ padding: '1rem', fontWeight: 600, textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {feeStructures.map(structure => (
                            <tr key={structure.id} style={{
                                borderBottom: '1px solid #e0e0e0',
                                transition: 'background-color 0.2s'
                            }}>
                                <td style={{ padding: '1rem' }}>{structure.department}</td>
                                <td style={{ padding: '1rem' }}>{structure.batch}</td>
                                <td style={{ padding: '1rem' }}>{structure.year || structure.academic_year}</td>
                                <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold', color: '#238548' }}>₹{totalFee(structure)}</td>
                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                    <button
                                        onClick={() => handleEditClick(structure)}
                                        style={{ marginRight: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}
                                        title="View/Edit"
                                    >
                                        👁️
                                    </button>
                                    <button
                                        onClick={() => handleEditClick(structure)}
                                        style={{ marginRight: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}
                                        title="Edit"
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(structure.id)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}
                                        title="Delete"
                                    >
                                        🗑️
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Add/Edit Modal */}
            {showAddModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        background: 'white',
                        padding: '2rem',
                        borderRadius: '12px',
                        width: '95%',
                        maxWidth: '1100px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                    }}>
                        <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#238548' }}>{isEditing ? 'Edit Fee Structure' : 'Add New Fee Structure'}</h3>
                        <form onSubmit={handleSaveStructure}>

                            {/* Year, Department, Batch Row */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Year</label>
                                    <select
                                        name="year"
                                        value={currentStructure.year || ''}
                                        onChange={handleInputChange}
                                        required
                                        disabled={yearsLoading}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: yearsLoading ? '#f5f5f5' : 'white' }}
                                    >
                                        <option value="">{yearsLoading ? 'Loading Years...' : 'Select Year'}</option>
                                        {years.map(yr => <option key={yr.value} value={yr.value}>{yr.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Department</label>
                                    <select
                                        name="department"
                                        value={currentStructure.department}
                                        onChange={handleInputChange}
                                        required
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map(dept => (
                                            <option key={dept.value} value={dept.value}>{dept.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Batch</label>
                                    <select
                                        name="batch"
                                        value={currentStructure.batch || ''}
                                        onChange={handleInputChange}
                                        required
                                        disabled={!currentStructure.department}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: !currentStructure.department ? '#f5f5f5' : 'white' }}
                                    >
                                        <option value="">{currentStructure.department ? 'Select Batch' : 'Select Dept First'}</option>
                                        {batches.map((b, i) => (
                                            <option key={i} value={b.batch_name || b}>{b.batch_name || b}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Semester */}
                            <div style={{ marginBottom: '1.5rem', width: '32%' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Semester</label>
                                <select
                                    name="semester"
                                    value={currentStructure.semester}
                                    onChange={handleInputChange}
                                    required
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                                >
                                    <option value="">Select Semester</option>
                                    {semesters.map(sem => <option key={sem.value} value={sem.value}>{sem.label}</option>)}
                                </select>
                            </div>

                            {/* One-Time Fees Section */}
                            <div style={{
                                background: '#f9f9f9',
                                padding: '1.5rem',
                                borderRadius: '8px',
                                marginBottom: '1.5rem',
                                border: '1px solid #e5e5e5'
                            }}>
                                <h4 style={{
                                    margin: '0 0 1rem 0',
                                    color: '#d32f2f',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '1rem',
                                    fontWeight: 600
                                }}>
                                    📌 One-Time Fees (At Admission)
                                </h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                    <div style={{ flex: '1 1 calc(25% - 0.75rem)', minWidth: '200px' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            color: '#666',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            ADMISSION FEES
                                        </label>
                                        <input
                                            type="number"
                                            name="admission_fee"
                                            value={currentStructure.admission_fee}
                                            onChange={handleInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                borderRadius: '6px',
                                                border: '1px solid #ddd',
                                                fontSize: '0.95rem'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            color: '#666',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            UNIFORM KIT
                                        </label>
                                        <input
                                            type="number"
                                            name="uniform_fee"
                                            value={currentStructure.uniform_fee}
                                            onChange={handleInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                borderRadius: '6px',
                                                border: '1px solid #ddd',
                                                fontSize: '0.95rem'
                                            }}
                                        />
                                    </div>
                                    <div style={{ flex: '1 1 calc(25% - 0.75rem)', minWidth: '200px' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            color: '#666',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            SKILL FEES
                                        </label>
                                        <input
                                            type="number"
                                            name="skill_fee"
                                            value={currentStructure.skill_fee}
                                            onChange={handleInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                borderRadius: '6px',
                                                border: '1px solid #ddd',
                                                fontSize: '0.95rem'
                                            }}
                                        />
                                    </div>
                                    <div style={{ flex: '1 1 calc(25% - 0.75rem)', minWidth: '200px' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            color: '#666',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            REGISTRATION FEES
                                        </label>
                                        <input
                                            type="number"
                                            name="registration_fee"
                                            value={currentStructure.registration_fee}
                                            onChange={handleInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                borderRadius: '6px',
                                                border: '1px solid #ddd',
                                                fontSize: '0.95rem'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Semester-wise Fees Section */}
                            <div style={{
                                background: '#f9f9f9',
                                padding: '1.5rem',
                                borderRadius: '8px',
                                border: '1px solid #e5e5e5'
                            }}>
                                <h4 style={{
                                    margin: '0 0 1rem 0',
                                    color: '#1976d2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '1rem',
                                    fontWeight: 600
                                }}>
                                    📅 Semester-wise Fees
                                </h4>

                                {/* Semester Tabs */}
                                <div style={{
                                    display: 'flex',
                                    gap: '0.5rem',
                                    marginBottom: '1.5rem',
                                    flexWrap: 'wrap',
                                    borderBottom: '2px solid #e0e0e0',
                                    paddingBottom: '0.5rem'
                                }}>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(sem => (
                                        <button
                                            key={sem}
                                            type="button"
                                            onClick={() => setActiveSemester(String(sem))}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                borderRadius: '6px 6px 0 0',
                                                border: 'none',
                                                background: activeSemester === String(sem) ? '#1976d2' : '#e0e0e0',
                                                color: activeSemester === String(sem) ? 'white' : '#666',
                                                cursor: 'pointer',
                                                fontWeight: activeSemester === String(sem) ? 600 : 400,
                                                fontSize: '0.85rem',
                                                transition: 'all 0.2s ease',
                                                minWidth: '80px'
                                            }}
                                        >
                                            {sem}{sem === 1 ? 'st' : sem === 2 ? 'nd' : sem === 3 ? 'rd' : 'th'} Sem
                                        </button>
                                    ))}
                                </div>

                                {/* Fee Inputs for Active Semester */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                    <div style={{ flex: '1 1 calc(33.33% - 0.67rem)', minWidth: '200px' }}>
                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Semester Fee</label>
                                        <input
                                            type="number"
                                            value={currentStructure.semester_fees[activeSemester].tuition_fee}
                                            onChange={(e) => handleSemesterFeeChange(activeSemester, 'tuition_fee', e.target.value)}
                                            style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                                        />
                                    </div>
                                    <div style={{ flex: '1 1 calc(33.33% - 0.67rem)', minWidth: '200px' }}>
                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Exam Fee</label>
                                        <input
                                            type="number"
                                            value={currentStructure.semester_fees[activeSemester].exam_fee}
                                            onChange={(e) => handleSemesterFeeChange(activeSemester, 'exam_fee', e.target.value)}
                                            style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                                        />
                                    </div>

                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: 'none', background: '#238548', color: 'white', cursor: 'pointer', fontWeight: 600 }}
                                >
                                    {isEditing ? 'Update Structure' : 'Save Structure'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default AccountsFeeStructure;
